import { useEffect, useRef } from 'react';

// AudioManager component to handle sound effects and background music
const AudioManager = ({
  onMobKilled,
  onAttack,
  onMove,
  onStairs,
  onPickup,
  currentZone,
}) => {
  // Refs to store audio objects for sound effects
  const soundEffects = useRef({
    mobKilled: null,
    attack: null,
    move: null,
    stairs: null,
    pickup: null,
  });

  // Ref to store the background music audio object
  const backgroundMusic = useRef(null);

  // Predefined soundtracks for each zone (extend this as needed)
  const zoneSoundtracks = useRef({
    'Crypt': '/audio/soundtracks/crypt_soundtrack.mp3',
    'Veilspires': '/audio/soundtracks/veilspires_soundtrack.mp3',
    'Hollow Depths': '/audio/soundtracks/hollow_depths_soundtrack.mp3',
    'Shattered Veil': '/audio/soundtracks/shattered_veil_soundtrack.mp3',
    'Cursed Expanse Wastes': '/audio/soundtracks/cursed_expance_wastes_soundtrack.mp3',
    'Shattered Vestibule': '/audio/soundtracks/shattered_vestibule_soundtrack.mp3',
  });

  // Load sound effects when the component mounts
  useEffect(() => {
    soundEffects.current.mobKilled = new Audio('/audio/effects/mob_killed.wav');
    soundEffects.current.attack = new Audio('/audio/effects/attack.wav');
    soundEffects.current.move = new Audio('/audio/effects/move.wav');
    soundEffects.current.stairs = new Audio('/audio/effects/stairs.wav');
    soundEffects.current.pickup = new Audio('/audio/effects/pickup.wav');

    // Preload and log loading status
    const preloadAudio = async (audio, name) => {
      try {
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
        console.log(`${name} audio preloaded successfully`);
      } catch (error) {
        console.error(`Failed to preload ${name} audio:`, error);
      }
    };

    Object.entries(soundEffects.current).forEach(([name, audio]) => preloadAudio(audio, name));

    // Set volume for sound effects
    Object.values(soundEffects.current).forEach((audio) => {
      audio.volume = 0.5; // 50% volume for sound effects
    });

    // Cleanup: Pause all audio when the component unmounts
    return () => {
      Object.values(soundEffects.current).forEach((audio) => {
        audio.pause();
      });
      if (backgroundMusic.current) {
        backgroundMusic.current.pause();
      }
    };
  }, []);

  // Play sound effects when triggered
  useEffect(() => {
    if (onMobKilled) {
      soundEffects.current.mobKilled.currentTime = 0;
      soundEffects.current.mobKilled.play().catch((error) => {
        console.error('Error playing mob_killed sound:', error);
      });
    }
  }, [onMobKilled]);

  useEffect(() => {
    if (onAttack) {
      soundEffects.current.attack.currentTime = 0;
      soundEffects.current.attack.play().catch((error) => {
        console.error('Error playing attack sound:', error);
      });
    }
  }, [onAttack]);

  useEffect(() => {
    if (onMove) {
      soundEffects.current.move.currentTime = 0;
      soundEffects.current.move.play().catch((error) => {
        console.error('Error playing move sound:', error);
      });
    }
  }, [onMove]);

  useEffect(() => {
    if (onStairs) {
      soundEffects.current.stairs.currentTime = 0;
      soundEffects.current.stairs.play().catch((error) => {
        console.error('Error playing stairs sound:', error);
      });
    }
  }, [onStairs]);

  useEffect(() => {
    if (onPickup) {
      soundEffects.current.pickup.currentTime = 0;
      soundEffects.current.pickup.play().catch((error) => {
        console.error('Error playing pickup sound:', error);
      });
    }
  }, [onPickup]);

  // Handle background music based on the current zone
  useEffect(() => {
    if (!currentZone) return;

    const region = currentZone.region;
    const soundtrackPath = zoneSoundtracks.current[region];

    if (soundtrackPath) {
      if (backgroundMusic.current) {
        backgroundMusic.current.pause();
        backgroundMusic.current = null;
      }

      backgroundMusic.current = new Audio(soundtrackPath);
      backgroundMusic.current.loop = true;
      backgroundMusic.current.volume = 0.3;
      backgroundMusic.current.play().catch((error) => {
        console.error(`Error playing soundtrack for ${region}:`, error);
        // If no soundtrack is found, log and continue without music
        if (error.name === 'NotSupportedError') {
          console.warn(`No soundtrack found for ${region}, skipping music.`);
        }
      });
    }

    return () => {
      if (backgroundMusic.current) {
        backgroundMusic.current.pause();
        backgroundMusic.current = null;
      }
    };
  }, [currentZone]);

  return null; // This component doesn't render anything
};

export default AudioManager;